import {
  TREASURY_FIELD_TYPES,
  TREASURY_FIELD_USAGES,
  TREASURY_MOVEMENT_TYPES,
} from "../constants/treasuryFormsOptions";

const SELECTION_TYPES = new Set([
  TREASURY_FIELD_TYPES.SINGLE_CHOICE,
  TREASURY_FIELD_TYPES.MULTIPLE_CHOICE,
]);
const MOVEMENT_FIELD_TYPES = new Set([
  TREASURY_FIELD_TYPES.AMOUNT,
  TREASURY_FIELD_TYPES.CONCEPT_AMOUNT_LIST,
]);

let localSequence = 0;

function makeLocalId(prefix = "field") {
  localSequence += 1;
  return `${prefix}-${Date.now()}-${localSequence}`;
}

export function makeOption(value = "") {
  return { localId: makeLocalId("option"), value };
}

export function makeTreasuryFormField(overrides = {}) {
  const fieldType = overrides.fieldType || TREASURY_FIELD_TYPES.SHORT_TEXT;
  const sourceOptions = Array.isArray(overrides.options)
    ? overrides.options
    : SELECTION_TYPES.has(fieldType)
    ? ["", ""]
    : [];

  return {
    localId: overrides.localId || makeLocalId(),
    id: overrides.id,
    label: overrides.label || "",
    fieldType,
    isRequired: Boolean(overrides.isRequired),
    options: sourceOptions.map((option) =>
      typeof option === "object" && option?.localId
        ? option
        : makeOption(typeof option === "string" ? option : "")
    ),
    numberFormat: overrides.numberFormat || "INTEGER",
    treasuryUsage:
      overrides.treasuryUsage || TREASURY_FIELD_USAGES.INFORMATIONAL,
    movementType: overrides.movementType || "",
    accountId:
      overrides.accountId === null || overrides.accountId === undefined
        ? ""
        : String(overrides.accountId),
    categoryId:
      overrides.categoryId === null || overrides.categoryId === undefined
        ? ""
        : String(overrides.categoryId),
    concept: overrides.concept || "",
    account: overrides.account || null,
    category: overrides.category || null,
  };
}

export function normalizeTreasuryFormDetail(detail) {
  const version = detail?.currentVersion || {};
  const fields = Array.isArray(version.fields)
    ? [...version.fields]
        .sort((a, b) => Number(a.orderIndex) - Number(b.orderIndex))
        .map((field) =>
          makeTreasuryFormField({
            ...field,
            numberFormat: field.settings?.numberFormat || "INTEGER",
          })
        )
    : [];

  return {
    metadata: {
      name: detail?.name || "",
      type: detail?.type || "",
      description: detail?.description || "",
    },
    fields: fields.length ? fields : [makeTreasuryFormField()],
    versionNumber: Number(version.versionNumber || 1),
  };
}

export function changeTreasuryFieldType(field, nextType) {
  const remainsSelection =
    SELECTION_TYPES.has(field.fieldType) && SELECTION_TYPES.has(nextType);
  const remainsMovement =
    MOVEMENT_FIELD_TYPES.has(field.fieldType) && MOVEMENT_FIELD_TYPES.has(nextType);

  return {
    ...field,
    fieldType: nextType,
    options: SELECTION_TYPES.has(nextType)
      ? remainsSelection && field.options.length >= 2
        ? field.options
        : [makeOption(), makeOption()]
      : [],
    numberFormat:
      nextType === TREASURY_FIELD_TYPES.NUMBER
        ? field.numberFormat || "INTEGER"
        : "INTEGER",
    treasuryUsage: MOVEMENT_FIELD_TYPES.has(nextType)
      ? remainsMovement
        ? field.treasuryUsage
        : TREASURY_FIELD_USAGES.INFORMATIONAL
      : TREASURY_FIELD_USAGES.INFORMATIONAL,
    movementType: remainsMovement ? field.movementType : "",
    accountId: remainsMovement ? field.accountId : "",
    categoryId: remainsMovement ? field.categoryId : "",
    concept: remainsMovement ? field.concept : "",
    account: remainsMovement ? field.account : null,
    category: remainsMovement ? field.category : null,
  };
}

export function categoryMatchesMovement(category, movementType) {
  if (!category || !movementType) return true;
  const scope = String(category.movementTypeScope || "").toUpperCase();
  return scope === "BOTH" || scope === movementType;
}

function isAvailableReference(list, id) {
  const reference = list.find((item) => String(item.id) === String(id));
  return Boolean(reference && reference.isActive && !reference.deletedAt);
}

export function validateTreasuryFormEditor(
  metadata,
  fields,
  { accounts = [], categories = [], requireActiveReferences = true } = {}
) {
  const metadataErrors = {};
  const fieldErrors = {};

  if (!metadata.name?.trim()) metadataErrors.name = "El nombre es obligatorio.";
  else if (metadata.name.trim().length > 255) {
    metadataErrors.name = "El nombre no puede superar 255 caracteres.";
  }
  if (!metadata.type) metadataErrors.type = "El tipo es obligatorio.";

  if (!Array.isArray(fields) || fields.length === 0) {
    metadataErrors.fields = "Agregá al menos un campo al formulario.";
  }

  (fields || []).forEach((field) => {
    const errors = {};
    if (!field.label?.trim()) errors.label = "El nombre del campo es obligatorio.";
    else if (field.label.trim().length > 255) {
      errors.label = "El nombre no puede superar 255 caracteres.";
    }

    if (SELECTION_TYPES.has(field.fieldType)) {
      if (!Array.isArray(field.options) || field.options.length < 2) {
        errors.options = "Agregá al menos dos opciones.";
      } else if (field.options.some((option) => !option.value?.trim())) {
        errors.options = "Las opciones no pueden estar vacías.";
      }
    }

    if (
      field.fieldType === TREASURY_FIELD_TYPES.NUMBER &&
      !["INTEGER", "DECIMAL"].includes(field.numberFormat)
    ) {
      errors.numberFormat = "Seleccioná entero o decimal.";
    }

    if (
      MOVEMENT_FIELD_TYPES.has(field.fieldType) &&
      field.treasuryUsage === TREASURY_FIELD_USAGES.MOVEMENT
    ) {
      if (!Object.values(TREASURY_MOVEMENT_TYPES).includes(field.movementType)) {
        errors.movementType = "Seleccioná ingreso o egreso.";
      }
      if (!field.accountId) errors.accountId = "Seleccioná una cuenta.";
      else if (
        requireActiveReferences &&
        !isAvailableReference(accounts, field.accountId)
      ) {
        errors.accountId = "Seleccioná una cuenta activa.";
      }
      if (!field.categoryId) errors.categoryId = "Seleccioná una categoría.";
      else {
        const category = categories.find(
          (item) => String(item.id) === String(field.categoryId)
        );
        if (requireActiveReferences && (!category?.isActive || category?.deletedAt)) {
          errors.categoryId = "Seleccioná una categoría activa.";
        } else if (!categoryMatchesMovement(category, field.movementType)) {
          errors.categoryId = "La categoría no es compatible con el movimiento.";
        }
      }
      if (!field.concept?.trim()) {
        errors.concept = "El concepto del movimiento es obligatorio.";
      } else if (field.concept.trim().length > 255) {
        errors.concept = "El concepto no puede superar 255 caracteres.";
      }
    }

    if (Object.keys(errors).length) fieldErrors[field.localId] = errors;
  });

  return {
    metadataErrors,
    fieldErrors,
    isValid:
      Object.keys(metadataErrors).length === 0 &&
      Object.keys(fieldErrors).length === 0,
  };
}

export function buildTreasuryFieldsPayload(fields) {
  return fields.map((field, index) => {
    const isSelection = SELECTION_TYPES.has(field.fieldType);
    const isMovementField = MOVEMENT_FIELD_TYPES.has(field.fieldType);
    const generatesMovement =
      isMovementField &&
      field.treasuryUsage === TREASURY_FIELD_USAGES.MOVEMENT;

    let settings = null;
    if (field.fieldType === TREASURY_FIELD_TYPES.SHORT_TEXT) {
      settings = { maxLength: 255 };
    } else if (field.fieldType === TREASURY_FIELD_TYPES.NUMBER) {
      settings = { numberFormat: field.numberFormat || "INTEGER" };
    } else if (field.fieldType === TREASURY_FIELD_TYPES.AMOUNT) {
      settings = { currency: "ARS" };
    } else if (field.fieldType === TREASURY_FIELD_TYPES.IMAGES) {
      settings = {
        maxFiles: 3,
        allowedMimeTypes: ["image/jpeg", "image/png"],
      };
    } else if (field.fieldType === TREASURY_FIELD_TYPES.CONCEPT_AMOUNT_LIST) {
      settings = { maxRows: 20 };
    }

    return {
      label: field.label.trim(),
      fieldType: field.fieldType,
      isRequired: Boolean(field.isRequired),
      orderIndex: index + 1,
      options: isSelection
        ? field.options.map((option) => option.value.trim())
        : null,
      settings,
      treasuryUsage: isMovementField
        ? field.treasuryUsage
        : TREASURY_FIELD_USAGES.INFORMATIONAL,
      movementType: generatesMovement ? field.movementType : null,
      accountId: generatesMovement ? Number(field.accountId) : null,
      categoryId: generatesMovement ? Number(field.categoryId) : null,
      concept: generatesMovement ? field.concept.trim() : null,
    };
  });
}

export function buildTreasuryFormPayload(metadata, fields, { includeFields = true } = {}) {
  const payload = {
    name: metadata.name.trim(),
    type: metadata.type,
    description: metadata.description?.trim() || null,
  };
  if (includeFields) payload.fields = buildTreasuryFieldsPayload(fields);
  return payload;
}

export function treasuryFieldsSignature(fields) {
  return JSON.stringify(buildTreasuryFieldsPayload(fields));
}

export function treasuryEditorSignature(metadata, fields) {
  return JSON.stringify(buildTreasuryFormPayload(metadata, fields));
}

export function mapTreasuryFormServerErrors(error, fields) {
  const message = Array.isArray(error?.data?.message)
    ? error.data.message.join(" | ")
    : error?.message || error?.data?.message || "No se pudo guardar el formulario.";
  const metadataErrors = {};
  const fieldErrors = {};

  if (/nombre del formulario|\bname\b/i.test(message)) {
    metadataErrors.name = message;
  }
  const matchedField = fields.find(
    (field) => field.label?.trim() && message.includes(`"${field.label.trim()}"`)
  );
  if (matchedField) {
    fieldErrors[matchedField.localId] = { server: message };
  }

  return { message, metadataErrors, fieldErrors };
}

export function isMovementCapableField(fieldType) {
  return MOVEMENT_FIELD_TYPES.has(fieldType);
}

export function isSelectionField(fieldType) {
  return SELECTION_TYPES.has(fieldType);
}
